import { useEffect, useId } from "react";

export interface Shortcut {
    /** Key chord parts, e.g. ["Ctrl", "C"]; each renders as its own key cap. */
    keys: string[];
    description: string;
}

export interface ShortcutsModalProps {
    open: boolean;
    onClose: () => void;
    shortcuts: Shortcut[];
    /** For `aria-controls` on the opener control. */
    dialogId: string;
    title?: string;
}

export function ShortcutsModal({
    open,
    onClose,
    shortcuts,
    dialogId,
    title = "Keyboard shortcuts",
}: ShortcutsModalProps) {
    const titleId = useId();

    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            window.removeEventListener("keydown", onKey);
            document.body.style.overflow = prev;
        };
    }, [open, onClose]);

    if (!open) return null;

    return (
        <>
            <div
                className="shortcuts-modal-backdrop"
                aria-hidden
                onClick={onClose}
            />
            <div
                id={dialogId}
                className="shortcuts-modal-dialog"
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
            >
                <div className="shortcuts-modal-header">
                    <span id={titleId} className="shortcuts-modal-title">
                        {title}
                    </span>
                    <button
                        type="button"
                        className="shortcuts-modal-close"
                        onClick={onClose}
                        aria-label="Close keyboard shortcuts"
                    >
                        x
                    </button>
                </div>
                <div className="shortcuts-modal-body app-scrollbar">
                    <div className="shortcuts-modal-table">
                        {shortcuts.map((s, i) => (
                            <div key={i} style={{ display: "contents" }}>
                                <span className="shortcuts-modal-keys">
                                    {s.keys.map((k, ki) => (
                                        <kbd
                                            key={ki}
                                            className="shortcuts-modal-key"
                                        >
                                            {k}
                                        </kbd>
                                    ))}
                                </span>
                                <span className="shortcuts-modal-desc">
                                    {s.description}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}
